import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';

import { accordionColorClasses } from '../lib/colorSchemes';
import { cn } from '../lib/utils';

type Variant = 'default' | 'ghost' | 'filled' | 'subtle';
type Color = 'primary' | 'secondary' | 'destructive' | 'warning' | 'neutral';

// ── Variants ──────────────────────────────────────────────────────────────────

const accordionVariants = tv({
  slots: {
    root: 'flex flex-col',
    item: 'overflow-hidden',
    trigger: [
      'group flex w-full items-center justify-between gap-3 text-left font-medium',
      'transition-colors [outline:none] focus-visible:ring-2 focus-visible:ring-inset',
      'disabled:pointer-events-none disabled:opacity-50'
    ],
    content: 'text-sm',
    icon: 'h-4 w-4 shrink-0 transition-transform duration-200'
  },
  variants: {
    variant: {
      default: {
        root: 'gap-2',
        item: 'rounded-lg border shadow-xs',
        trigger: 'px-4 py-3',
        content: 'border-t px-4 pb-4 pt-3',
        icon: ''
      },
      ghost: {
        root: 'divide-y',
        item: '',
        trigger: 'px-4 py-3 rounded-md',
        content: 'px-4 pb-4 pt-3',
        icon: ''
      },
      subtle: {
        root: 'gap-2',
        item: 'rounded-lg border overflow-hidden',
        trigger: 'px-4 py-3',
        content: 'border-t px-4 pb-4 pt-3 bg-white dark:bg-uniform-gray-900',
        icon: ''
      },
      filled: {
        root: 'gap-2',
        item: 'rounded-lg overflow-hidden shadow-xs',
        trigger: 'px-4 py-3',
        content: 'px-4 pb-4 pt-3',
        icon: ''
      }
    },
    color: {
      neutral: accordionColorClasses.neutral.base,
      primary: accordionColorClasses.primary.base,
      secondary: accordionColorClasses.secondary.base,
      destructive: accordionColorClasses.destructive.base,
      warning: accordionColorClasses.warning.base
    }
  },
  compoundVariants: [
    // default: item border + content separator
    { variant: 'default', color: 'neutral', class: accordionColorClasses.neutral.default },
    { variant: 'default', color: 'primary', class: accordionColorClasses.primary.default },
    { variant: 'default', color: 'secondary', class: accordionColorClasses.secondary.default },
    { variant: 'default', color: 'destructive', class: accordionColorClasses.destructive.default },
    { variant: 'default', color: 'warning', class: accordionColorClasses.warning.default },
    // ghost: root divider color
    { variant: 'ghost', color: 'neutral', class: accordionColorClasses.neutral.ghost },
    { variant: 'ghost', color: 'primary', class: accordionColorClasses.primary.ghost },
    { variant: 'ghost', color: 'secondary', class: accordionColorClasses.secondary.ghost },
    { variant: 'ghost', color: 'destructive', class: accordionColorClasses.destructive.ghost },
    { variant: 'ghost', color: 'warning', class: accordionColorClasses.warning.ghost },
    // subtle: tinted bg + colored text
    { variant: 'subtle', color: 'neutral', class: accordionColorClasses.neutral.subtle },
    { variant: 'subtle', color: 'primary', class: accordionColorClasses.primary.subtle },
    { variant: 'subtle', color: 'secondary', class: accordionColorClasses.secondary.subtle },
    { variant: 'subtle', color: 'destructive', class: accordionColorClasses.destructive.subtle },
    { variant: 'subtle', color: 'warning', class: accordionColorClasses.warning.subtle },
    // filled: solid colored header + tinted content
    { variant: 'filled', color: 'neutral', class: accordionColorClasses.neutral.filled },
    { variant: 'filled', color: 'primary', class: accordionColorClasses.primary.filled },
    { variant: 'filled', color: 'secondary', class: accordionColorClasses.secondary.filled },
    { variant: 'filled', color: 'destructive', class: accordionColorClasses.destructive.filled },
    { variant: 'filled', color: 'warning', class: accordionColorClasses.warning.filled }
  ],
  defaultVariants: {
    variant: 'default',
    color: 'neutral'
  }
});

type AccordionVariants = VariantProps<typeof accordionVariants>;

// ── Context ───────────────────────────────────────────────────────────────────

type AccordionCtx = { variant: Variant; color: Color };

const AccordionContext = createContext<AccordionCtx>({ variant: 'default', color: 'neutral' });

type AccordionItemCtx = AccordionCtx & { open: boolean };

const AccordionItemContext = createContext<AccordionItemCtx>({
  variant: 'default',
  color: 'neutral',
  open: false
});

// ── Accordion (root wrapper) ──────────────────────────────────────────────────

interface AccordionProps {
  variant?: Variant;
  color?: Color;
  children?: ReactNode;
  className?: string;
}

function Accordion({
  variant = 'default',
  color = 'neutral',
  children,
  className
}: AccordionProps) {
  const { root } = accordionVariants({ variant, color });
  return (
    <AccordionContext.Provider value={{ variant, color }}>
      <div className={cn(root(), className)}>{children}</div>
    </AccordionContext.Provider>
  );
}

// ── AccordionItem ─────────────────────────────────────────────────────────────

interface AccordionItemProps {
  children?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
  /** Override variant from parent Accordion */
  variant?: Variant;
  /** Override color from parent Accordion */
  color?: Color;
  /** Fires whenever the disclosure's open state changes -- e.g. to lazily
   * fetch a section's contents the first time it's expanded. */
  onOpenChange?: (open: boolean) => void;
}

function AccordionItemOpenListener({
  open,
  onOpenChange
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  useEffect(() => onOpenChange(open), [open, onOpenChange]);
  return null;
}

function AccordionItem({
  children,
  defaultOpen = false,
  className,
  variant: vProp,
  color: cProp,
  onOpenChange
}: AccordionItemProps) {
  const ctx = useContext(AccordionContext);
  const variant = vProp ?? ctx.variant;
  const color = cProp ?? ctx.color;
  const { item } = accordionVariants({ variant, color });

  return (
    <Disclosure defaultOpen={defaultOpen}>
      {({ open }) => (
        <AccordionItemContext.Provider value={{ variant, color, open }}>
          <div className={cn(item(), className)}>
            {onOpenChange && <AccordionItemOpenListener open={open} onOpenChange={onOpenChange} />}
            {children}
          </div>
        </AccordionItemContext.Provider>
      )}
    </Disclosure>
  );
}

// ── AccordionTrigger ──────────────────────────────────────────────────────────

interface AccordionTriggerProps {
  children?: ReactNode;
  className?: string;
  'aria-label'?: string;
  /** `end` pins the chevron to the far edge; `inline` sets it right after the label, which
   * truncates, leaving the rest of the row free for the caller. */
  iconPlacement?: 'end' | 'inline';
  onClick?: () => void;
}

function AccordionTrigger({
  children,
  className,
  'aria-label': ariaLabel,
  iconPlacement = 'end',
  onClick
}: AccordionTriggerProps) {
  const { variant, color, open } = useContext(AccordionItemContext);
  const { trigger, icon } = accordionVariants({ variant, color });

  if (iconPlacement === 'inline') {
    return (
      <DisclosureButton
        aria-label={ariaLabel}
        className={cn(trigger(), 'justify-start gap-2', className)}
        onClick={onClick}>
        <span className="min-w-0 truncate">{children}</span>
        <FontAwesomeIcon
          icon={faChevronDown}
          className={cn(icon(), 'h-3 w-3 text-uniform-gray-500', !open && '-rotate-90')}
        />
      </DisclosureButton>
    );
  }

  return (
    <DisclosureButton aria-label={ariaLabel} className={cn(trigger(), className)} onClick={onClick}>
      <span className="flex-1 min-w-0">{children}</span>
      <FontAwesomeIcon icon={faChevronDown} className={cn(icon(), open && 'rotate-180')} />
    </DisclosureButton>
  );
}

// ── AccordionContent ──────────────────────────────────────────────────────────

interface AccordionContentProps {
  children?: ReactNode;
  className?: string;
}

function AccordionContent({ children, className }: AccordionContentProps) {
  const { variant, color, open } = useContext(AccordionItemContext);
  const { content } = accordionVariants({ variant, color });

  return (
    // Grid-rows trick: animate height by transitioning grid-template-rows 1fr ↔ 0fr
    <div
      className={cn(
        'grid transition-[grid-template-rows] duration-200 ease-in-out',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}>
      <div className="overflow-hidden">
        <DisclosurePanel static className={cn(content(), className)}>
          {children}
        </DisclosurePanel>
      </div>
    </div>
  );
}

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent, accordionVariants };
export type {
  AccordionProps,
  AccordionItemProps,
  AccordionTriggerProps,
  AccordionContentProps,
  AccordionVariants
};
